import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Custom Components */
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LectureTimer from "../../../components/LectureTimer";
import LanguageTabs from "../../../components/LanguageTabs";

/* Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "white",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const DashboardCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* Data Constants */
const javaTopics = [
    { label: "Learning Journey", to: "/learning-journey" },
    { label: "Data type, Variable and Control Structure", to: "/datatype-variable-controlstructure" },
    { label: "Methods and Scopes", to: "/method-and-scope" },
    { label: "Object Oriented Programming", to: "/java-oop" },
    { label: "Arrays and Strings", to: "/java-arrays-strings" },
    { label: "Exception Handling", to: "/java-exception-handling" },
    { label: "Java File Handling", to: "/java-file-handling" },
    { label: "Collections", to: "/java-collections" },
    { label: "Interfaces and Abstract Classes", to: "/java-interfaces-abstract-classes" },
    { label: "Lambda Expressions", to: "/java-lambda-expressions" },
    { label: "Multithreading", to: "/java-multithreading" },
    { label: "Java Quiz", to: "/java-quiz" },
    { label: "Java Assignment", to: "/java-assignments" },
    { label: "Java Assignment Solution", to: "/java-assignment-solutions" },
    { label: "Certification Exam", to: "/java-certification-exam" },
];

/* Main Component */
export default function JavaCoding() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State */
    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);

    // Progress State (Defaulting to 0 for a fresh start)
    const [completedCount, setCompletedCount] = useState(0);

    /* Logic for Filtering & Scroll */
    const filteredTopics = useMemo(() => {
        return javaTopics.filter(topic =>
            topic.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    // Calculate progress percentage
    const progressPercentage = (completedCount / javaTopics.length) * 100;

    React.useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <PageWrapper>
            {/* ---------------- GOLDEN BANNER ---------------- */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 700,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA PROGRAMMING
                </Typography>
            </Box>

            {/* ---------------- TIMER & TABS ---------------- */}
            <LectureTimer />
            <LanguageTabs active="Java"/>
            {/* ---------------- MAIN CONTENT ---------------- */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* Left Panel: Video & Compiler Link */}
                <Grid item xs={12} md={5}>
                    <Box sx={{
                        backgroundColor: "rgba(16,7,32,0.85)",
                        borderRadius: 3,
                        p: { xs: 2, md: 3 },
                        border: "1px solid rgba(255,255,255,0.1)"
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}>
                            SELF-STUDY VIDEO
                        </Typography>

                        <DashboardCard>
                            <Box sx={{ p: { xs: 1, md: 2 }, backgroundColor: "#f5f5f5" }}>
                                <CardMedia
                                    component="iframe"
                                    height={isMobile ? "200" : "280"}
                                    src="https://www.youtube.com/embed/xk4_1vDrzzo"
                                    title="Free Java Tutorial"
                                    allowFullScreen
                                    sx={{ borderRadius: "8px", border: "none" }}
                                />

                                <CardContent sx={{ textAlign: "center", color: "#100720" }}>
                                    <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                                        Mastering Java Development
                                    </Typography>

                                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                        <PersonalVideoIcon fontSize="small" />
                                        <Typography variant="body2" fontWeight={600}>
                                            Mode: Online Self‑paced
                                        </Typography>
                                    </Stack>

                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                bgcolor: "#100720",
                                                color: "white",
                                                fontWeight: "bold",
                                                py: 1.5,
                                                "&:hover": { bgcolor: "#1a0c36" }
                                            }}
                                        >
                                            Open Hands-on Compiler
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Box>
                        </DashboardCard>
                    </Box>
                </Grid>

                {/* Right Panel: Topics with Search */}
                <Grid item xs={12} md={7}>
                    <Box sx={{
                        backgroundColor: "rgba(16,7,32,0.85)",
                        borderRadius: 3,
                        p: { xs: 2, md: 4 },
                        border: "1px solid rgba(255,255,255,0.1)"
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 1 }}>
                            JAVA CURRICULUM
                        </Typography>

                        {/* Progress Bar Section */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    Course Progress
                                </Typography>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    {Math.round(progressPercentage)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { bgcolor: "goldenrod" }
                                }}
                            />
                        </Box>

                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Find a topic..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(218,165,32,0.3)" }
                                }
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredTopics.length > 0 ? (
                                filteredTopics.map((topic) => (
                                    <TopicBox key={topic.to}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {topic.label}
                                            </Typography>
                                        </Box>
                                        <Link to={topic.to} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">View</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No topics found matching "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Grid>
            </Grid>

            {/* Floating Scroll to Top */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{
                        position: "fixed",
                        bottom: 25,
                        right: 25,
                        bgcolor: "goldenrod",
                        "&:hover": { bgcolor: "#e0b200" }
                    }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
